import apiClient from '../utils/apiClient';

/**
 * Menu Item API Services
 * Quản lý món ăn
 */

const buildMenuItemPayload = (data = {}) => {
  const payload = {};
  if (data.name !== undefined) payload.name = data.name;
  if (data.slug !== undefined) payload.slug = data.slug;
  if (data.description !== undefined) payload.description = data.description;
  if (data.price !== undefined) payload.price = data.price;
  if (data.image !== undefined) payload.image = data.image;
  if (data.categoryId !== undefined) payload.category_id = data.categoryId;
  if (data.enabledModifierGroupIds !== undefined) {
    payload.enabled_modifier_group_ids = data.enabledModifierGroupIds;
  }
  if (data.isAvailable !== undefined) payload.is_available = data.isAvailable;
  if (data.isFeatured !== undefined) payload.is_featured = data.isFeatured;
  if (data.displayOrder !== undefined) payload.display_order = data.displayOrder;
  if (data.tags !== undefined) payload.tags = data.tags;
  if (data.createdBy !== undefined) payload.created_by = data.createdBy || null;
  if (data.videoUrls !== undefined) payload.video_urls = (data.videoUrls || []).filter(Boolean);
  if (data.recipeHtml !== undefined) payload.recipe_html = data.recipeHtml;
  return payload;
};

export const menuItemAPI = {
  /**
   * Lấy danh sách món
   * @param {Object} params - Tham số truy vấn
   * @param {string} params.categoryId - Lọc theo danh mục
   * @param {boolean} params.isAvailable - Lọc theo trạng thái có sẵn
   * @param {boolean} params.isFeatured - Lọc món nổi bật
   * @returns {Promise<Object>} Danh sách món
   */
  getMenuItems: async (params = {}) => {
    return await apiClient.get({
      url: '/menu-items',
      params,
    });
  },

  /**
   * Lấy thông tin chi tiết một món
   * @param {string} itemId - ID món
   * @returns {Promise<Object>} Thông tin chi tiết món
   */
  getMenuItemById: async (itemId) => {
    return await apiClient.get({
      url: `/menu-items/${itemId}`,
    });
  },

  /**
   * Tạo món mới
   * @param {Object} itemData - Dữ liệu món
   * @param {string} itemData.name - Tên món
   * @param {string} itemData.slug - Slug món
   * @param {string} itemData.description - Mô tả
   * @param {number} itemData.price - Giá
   * @param {string} itemData.image - URL hình ảnh
   * @param {string} itemData.categoryId - ID danh mục
   * @param {Array<string>} itemData.enabledModifierGroupIds - Danh sách ID nhóm tùy chọn
   * @param {boolean} itemData.isAvailable - Trạng thái có sẵn
   * @param {boolean} itemData.isFeatured - Món nổi bật
   * @param {number} itemData.displayOrder - Thứ tự hiển thị
   * @param {Array<string>} itemData.tags - Tags
   * @returns {Promise<Object>} Thông tin món vừa tạo
   */
  createMenuItem: async (itemData) => {
    return await apiClient.post({
      url: '/menu-items',
      data: buildMenuItemPayload(itemData),
    });
  },

  /**
   * Cập nhật thông tin món
   * @param {string} itemId - ID món
   * @param {Object} updateData - Dữ liệu cập nhật
   * @returns {Promise<Object>} Thông tin món sau khi cập nhật
   */
  updateMenuItem: async (itemId, updateData) => {
    return await apiClient.put({
      url: `/menu-items/${itemId}`,
      data: buildMenuItemPayload(updateData),
    });
  },

  /**
   * Xóa món
   * @param {string} itemId - ID món
   * @returns {Promise<Object>} Kết quả xóa
   */
  deleteMenuItem: async (itemId) => {
    return await apiClient.delete({
      url: `/menu-items/${itemId}`,
    });
  },

  /**
   * Sắp xếp lại thứ tự món trong danh mục
   * @param {string} categoryId - ID danh mục
   * @param {Array<string>} menuItemIds - Mảng ID món theo thứ tự mới
   * @returns {Promise<Object>} Kết quả sắp xếp
   */
  reorderMenuItems: async (categoryId, menuItemIds) => {
    return await apiClient.patch({
      url: '/menu-items/reorder',
      data: { category_id: categoryId, menu_item_ids: menuItemIds },
    });
  },

  /**
   * Lấy danh sách món đang chờ duyệt
   * @returns {Promise<Object>} Danh sách món PENDING
   */
  getPendingMenuItems: async () => {
    return await apiClient.get({ url: '/menu-items/pending' });
  },

  /**
   * Duyệt hoặc từ chối món
   * @param {string} itemId - ID món
   * @param {'APPROVE'|'REJECT'} action - Hành động
   * @param {string} [rejectionReason] - Lý do từ chối (bắt buộc nếu REJECT)
   * @returns {Promise<Object>} Thông tin món sau khi xử lý
   */
  approveMenuItem: async (itemId, action, rejectionReason) => {
    const data = { action };
    if (rejectionReason) data.rejection_reason = rejectionReason;
    return await apiClient.post({ url: `/menu-items/${itemId}/approve`, data });
  },

  /**
   * Lấy danh sách tất cả món với filter approvalStatus
   * @param {Object} params
   * @returns {Promise<Object>}
   */
  getAllMenuItems: async (params = {}) => {
    return await apiClient.get({ url: '/menu-items', params });
  },

  toggleRequiresTraining: async (itemId, requiresTraining) => {
    return await apiClient.patch({
      url: `/menu-items/${itemId}/requires-training`,
      data: { requires_training: requiresTraining },
    });
  },

  // Training releases
  getReleases: async (params = {}) => {
    return await apiClient.get({ url: '/menu-training/releases', params });
  },

  createRelease: async (menuItemId, branchId, note) => {
    return await apiClient.post({
      url: '/menu-training/releases',
      data: { menu_item_id: menuItemId, branch_id: branchId, note },
    });
  },

  deleteRelease: async (releaseId) => {
    return await apiClient.delete({ url: `/menu-training/releases/${releaseId}` });
  },

  getReleaseStatus: async (menuItemId) => {
    return await apiClient.get({ url: `/menu-items/${menuItemId}/release-status` });
  },

  releaseAll: async (menuItemId, payload) => {
    return await apiClient.post({ url: `/menu-items/${menuItemId}/release-all`, data: payload });
  },

  // Training progress
  getTrainingDashboard: async (menuItemId, branchId) => {
    return await apiClient.get({
      url: `/menu-training/items/${menuItemId}/creator-dashboard`,
      params: branchId ? { branch_id: branchId } : {},
    });
  },

  getSkillMatrix: async (branchId) => {
    return await apiClient.get({
      url: '/menu-training/matrix',
      params: branchId ? { branch_id: branchId } : {},
    });
  },

  getItemSkillHistory: async (menuItemId, employeeId) => {
    return await apiClient.get({
      url: `/menu-training/items/${menuItemId}/history`,
      params: employeeId ? { employee_id: employeeId } : {},
    });
  },
};
